import { Pipe, PipeTransform } from '@angular/core';
import { formatDate } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { isUnsetDate } from './issue-formatting';

// Localized relative time for the Issues UI, per the copy convention:
//   < 1m -> "just now"; < 60m -> "Xm ago"; same day -> "Xh ago";
//   previous calendar day -> "yesterday 4:12 PM"; <= 7d -> "Xd ago";
//   older -> explicit short date.
// The existing `timeAgo` pipe can't express "yesterday {time}" / the compact
// forms, hence a dedicated pipe. Impure so it refreshes as the clock moves.
@Pipe({ name: 'issueTime', pure: false, standalone: false })
export class IssueRelativeTimePipe implements PipeTransform {
  constructor(private readonly translate: TranslateService) {}

  transform(value: string | null | undefined): string {
    if (isUnsetDate(value)) return '';
    const lang = this.translate.currentLang || 'en-US';
    const then = new Date(value as string);
    const now = new Date();
    const diffMs = now.getTime() - then.getTime();
    const min = Math.floor(diffMs / 60000);

    if (min < 1) return this.translate.instant('@issueJustNow');
    if (min < 60) return this.translate.instant('@issueMinutesAgo', { n: min });

    const sameDay = then.toDateString() === now.toDateString();
    if (sameDay) {
      return this.translate.instant('@issueHoursAgo', { n: Math.floor(min / 60) });
    }

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (then.toDateString() === yesterday.toDateString()) {
      return this.translate.instant('@issueYesterdayAt', {
        time: formatDate(then, 'shortTime', lang),
      });
    }

    const days = Math.floor(diffMs / 86400000);
    if (days <= 7) return this.translate.instant('@issueDaysAgo', { n: days });

    return formatDate(then, 'shortDate', lang);
  }
}
